/**
 * useLiveRace Hook
 *
 * React hook for subscribing to live race updates from the LTS system.
 * Handles Socket.IO room management and provides real-time race data.
 *
 * Usage:
 * const {
 *   status,           // Current race status (flag, laps, etc.)
 *   competitors,      // Current competitor positions
 *   isConnected,      // Socket.IO connection status
 *   ltsConnected,     // LTS WebSocket connection status
 *   lastUpdate,       // Timestamp of last update
 *   subscribe,        // Call to start watching this race via API
 *   unsubscribe,      // Call to stop watching this race
 *   refresh           // Manually refresh data from LTS
 * } = useLiveRace(tpeId);
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSocket } from '../contexts/SocketContext';
import { useApi } from '../contexts/ApiContext';

const useLiveRace = (tpeId) => {
  const { socket, isConnected, ltsConnected, joinRace, leaveRace, subscribe: socketSubscribe, unsubscribe: socketUnsubscribe } = useSocket();
  const api = useApi();

  const [status, setStatus] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Subscribe to LTS updates for this race via API
  const subscribeToRace = useCallback(async () => {
    if (!tpeId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await api.lts.subscribe(tpeId);
      if (result.success) {
        // Fetch current status after subscribing
        const statusResult = await api.lts.getStatus(tpeId);
        if (statusResult.success && statusResult.data?.data?.status) {
          setStatus(statusResult.data.data.status);
        }
        // Fetch competitors
        const competitorsResult = await api.lts.getCompetitors(tpeId);
        if (competitorsResult.success && competitorsResult.data?.data?.competitors) {
          setCompetitors(competitorsResult.data.data.competitors);
        }
        setLastUpdate(new Date());
      } else {
        setError(result.message || 'Failed to subscribe to race');
      }
    } catch (err) {
      setError(err.message || 'Failed to subscribe to race');
    } finally {
      setIsLoading(false);
    }
  }, [tpeId, api.lts]);

  // Unsubscribe from LTS updates
  const unsubscribeFromRace = useCallback(async () => {
    if (!tpeId) return;

    setIsLoading(true);
    try {
      await api.lts.unsubscribe(tpeId);
      setStatus(null);
      setCompetitors([]);
      setLastUpdate(null);
    } catch (err) {
      console.error('Failed to unsubscribe:', err);
    } finally {
      setIsLoading(false);
    }
  }, [tpeId, api.lts]);

  // Manually refresh data from LTS
  const refreshData = useCallback(async () => {
    if (!tpeId) return;

    setIsLoading(true);
    try {
      const result = await api.lts.refresh(tpeId);
      if (result.success) {
        setLastUpdate(new Date());
        // Fetch the updated status from LTS
        const statusResult = await api.lts.getStatus(tpeId);
        if (statusResult.success && statusResult.data?.data?.status) {
          setStatus(statusResult.data.data.status);
        }
        // Fetch competitors
        const competitorsResult = await api.lts.getCompetitors(tpeId);
        if (competitorsResult.success && competitorsResult.data?.data?.competitors) {
          setCompetitors(competitorsResult.data.data.competitors);
        }
      }
    } catch (err) {
      console.error('Failed to refresh:', err);
    } finally {
      setIsLoading(false);
    }
  }, [tpeId, api.lts]);

  // Handle race status updates
  const handleStatusUpdate = useCallback((data) => {
    if (data.tpeId === tpeId || data.tpeId === String(tpeId)) {
      console.log('[useLiveRace] Status update:', data);
      setStatus(data.status);
      setLastUpdate(new Date(data.timestamp));
    }
  }, [tpeId]);

  // Handle competitor position updates
  const handlePositionsUpdate = useCallback((data) => {
    if (data.tpeId === tpeId || data.tpeId === String(tpeId)) {
      console.log('[useLiveRace] Positions update:', data);
      setCompetitors(data.competitors);
      setLastUpdate(new Date(data.timestamp));
    }
  }, [tpeId]);

  // Handle passing events
  const handlePassingEvent = useCallback((data) => {
    if (data.tpeId === tpeId || data.tpeId === String(tpeId)) {
      console.log('[useLiveRace] Passing event:', data);
      setCompetitors(data.competitors);
      setLastUpdate(new Date(data.timestamp));
    }
  }, [tpeId]);

  // Join/leave Socket.IO room and set up event listeners
  useEffect(() => {
    if (!tpeId || !socket || !isConnected) return;

    // Join the race room
    joinRace(tpeId);

    // Subscribe to race events
    socketSubscribe('race:status', handleStatusUpdate);
    socketSubscribe('race:positions', handlePositionsUpdate);
    socketSubscribe('race:passing', handlePassingEvent);

    // Cleanup: leave room and unsubscribe from events
    return () => {
      leaveRace(tpeId);
      socketUnsubscribe('race:status', handleStatusUpdate);
      socketUnsubscribe('race:positions', handlePositionsUpdate);
      socketUnsubscribe('race:passing', handlePassingEvent);
    };
  }, [tpeId, socket, isConnected, joinRace, leaveRace, socketSubscribe, socketUnsubscribe, handleStatusUpdate, handlePositionsUpdate, handlePassingEvent]);

  const { isLoading: initialLoading } = useQuery({
    queryKey: ['live-race-initial', tpeId],
    queryFn: async () => {
      const [statusResult, competitorsResult] = await Promise.all([
        api.lts.getStatus(tpeId),
        api.lts.getCompetitors(tpeId)
      ]);

      if (statusResult.success && statusResult.data?.data?.status) {
        setStatus(statusResult.data.data.status);
        setLastUpdate(new Date());
      }

      if (competitorsResult.success && competitorsResult.data?.data?.competitors) {
        setCompetitors(competitorsResult.data.data.competitors);
      }

      return { status: statusResult.data, competitors: competitorsResult.data };
    },
    enabled: !!tpeId,
  });

  return {
    status,
    competitors,
    isConnected,
    ltsConnected,
    lastUpdate,
    isLoading: isLoading || initialLoading,
    error,
    subscribe: subscribeToRace,
    unsubscribe: unsubscribeFromRace,
    refresh: refreshData
  };
};

export default useLiveRace;
